import { useEffect, useRef, useState } from "react";
import { LaunchError } from "../utils/errors";
import { useDispatchApp } from "../lib/redux";
import { generateRecommendedLeadForm } from "../services/generateRecommendedLeadForm";
import {
  buildGenerateRecommendedLeadFormBody,
  loadRecommendedLeadForm,
  reportLeadFormRecommendationFailure,
} from "../utils/recommendedLeadForm";

const IDLE_RECOMMENDATION = {
  status: "idle",
  isGenerating: false,
  initialValues: null,
  notice: null,
  applyID: 0,
};

const DEFAULTS_RECOMMENDATION = { ...IDLE_RECOMMENDATION, status: "defaults" };

const languageKeyOf = (language) => {
  if (typeof language === "string") return language;
  try {
    return JSON.stringify(language ?? "");
  } catch {
    return "";
  }
};

// enabled is false when the editor already has initial values (stored
// recommendation or duplicate) or is read-only, so those never call.
export const useRecommendedLeadForm = ({
  platform,
  adID,
  adGroupID,
  enabled = false,
  campaignID,
  advertiserID,
  language,
  getCampaignValues,
  setCampaignValue,
} = {}) => {
  const dispatch = useDispatchApp();
  const [recommendation, setRecommendation] = useState(IDLE_RECOMMENDATION);
  const ignoredRef = useRef(false);
  const runIDRef = useRef(0);
  const getCampaignValuesRef = useRef(getCampaignValues);
  const setCampaignValueRef = useRef(setCampaignValue);
  const dispatchRef = useRef(dispatch);
  getCampaignValuesRef.current = getCampaignValues;
  setCampaignValueRef.current = setCampaignValue;
  dispatchRef.current = dispatch;

  const isEnabled = Boolean(enabled && campaignID && advertiserID);
  const languageKey = languageKeyOf(language);

  const onUseDefaults = () => {
    ignoredRef.current = true;
    setRecommendation(DEFAULTS_RECOMMENDATION);
  };

  useEffect(() => {
    if (!isEnabled) return undefined;

    const runID = ++runIDRef.current;
    ignoredRef.current = false;
    let cancelled = false;

    const load = async () => {
      const campaign = getCampaignValuesRef.current?.() || {};
      const body = buildGenerateRecommendedLeadFormBody({
        campaign,
        platform,
        adID,
        adGroupID,
        advertiserID,
        campaignID,
        language,
      });
      const outcome = await loadRecommendedLeadForm({
        platform,
        adID,
        adsGroups: campaign?.adsGroups,
        language,
        recommendations: campaign?.platformsData?.[platform]?.recommendations,
        shouldApply: () => !ignoredRef.current,
        setValue: (...args) => setCampaignValueRef.current?.(...args),
        request: () =>
          body
            ? generateRecommendedLeadForm({
                dispatch: dispatchRef.current,
                body,
                campaignID,
                platform,
              })
            : Promise.resolve({
                ok: false,
                status: 400,
                code: "missingParameters",
              }),
      });

      if (outcome.report) {
        reportLeadFormRecommendationFailure({
          dispatch: dispatchRef.current,
          launchError: LaunchError,
          report: outcome.report,
        });
      }
      if (cancelled || ignoredRef.current || runID !== runIDRef.current) {
        return;
      }
      setRecommendation({
        status: outcome.initialValues ? "ready" : "defaults",
        isGenerating: false,
        initialValues: outcome.initialValues,
        notice: outcome.notice,
        applyID: outcome.initialValues ? runID : 0,
      });
    };

    load();

    return () => {
      cancelled = true;
    };
    // languageKey stands in for language so a new array identity does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEnabled,
    platform,
    adID,
    adGroupID,
    campaignID,
    advertiserID,
    languageKey,
  ]);

  if (!isEnabled) return { ...IDLE_RECOMMENDATION, onUseDefaults };
  if (recommendation.status === "idle") {
    return {
      ...IDLE_RECOMMENDATION,
      status: "loading",
      isGenerating: true,
      onUseDefaults,
    };
  }
  return { ...recommendation, onUseDefaults };
};

export const useApplyRecommendedLeadForm = ({
  recommendation,
  reset,
  getValues,
}) => {
  const appliedIDRef = useRef(0);

  useEffect(() => {
    if (!recommendation?.applyID || !recommendation.initialValues) return;
    if (appliedIDRef.current === recommendation.applyID) return;
    appliedIDRef.current = recommendation.applyID;
    reset({
      ...getValues(),
      ...recommendation.initialValues,
    });
  }, [
    recommendation?.applyID,
    recommendation?.initialValues,
    reset,
    getValues,
  ]);
};
